import moment from 'moment';

const _MS_PER_DAY = 1000 * 60 * 60 * 24;

export function dateDiffInDays(a: Date, b: Date): number {
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

  return Math.floor((utc2 - utc1) / _MS_PER_DAY);
}

export const getDateDiffInDays = (a: Date, b: Date): number =>
  moment(a).diff(moment(b), 'days');

export const getIsPastDueDate = (dueDate: Date): boolean => {
  if (!dueDate) {
    return false;
  }
  const dueDateMoment = moment(new Date(dueDate));
  return moment.isMoment(dueDateMoment)
    ? getDate().isAfter(dueDateMoment.add(1, 'day'))
    : false;
};

export function dateDiffInMonths(a: Date, b: Date): number {
  const months = moment(b).diff(moment(a), 'months', true);
  return Math.floor(months);
}

export function formatDate(date: string, intl: Object) {
  if (date)
    return intl.formatDate(date, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
}

export function formatTime(time: string, intl: Object) {
  if (time) return intl.formatTime(time);
}

export const formatDateUTC = (date) => moment.utc(date).format('YYYY-MM-DD');

export const formatDateLocale = (date: Date, format: string = 'YYYY-MM-DD') =>
  date && format.length ? moment(date).local().format(format) : '';

/*BE returns dateTime strings in UTC, so initialize moment as utc then
 convert to local tz only, not actual date */
export const formatDateUTCToLocale = (date) =>
  moment.utc(date).local().format('YYYY-MM-DD');

export const getDateUTC = () => moment.utc();

export const getDate = () => moment();

const CALENDAR_DATE_PARSE_FORMATS = ['YYYY-MM-DD', moment.ISO_8601];

/**
 * Parse a value as a calendar day in UTC — the day itself, not a point in
 * time. Avoids local-timezone day shifts for YYYY-MM-DD and ISO midnight
 * timestamps (e.g. a birthday, or a startDate/endDate column that's a full
 * timestamptz but semantically only ever means a specific day).
 * Returns null for empty/invalid values.
 */
export const parseCalendarDateUtc = (value: ?(string | Date)) => {
  if (value == null || value === '') {
    return null;
  }
  const parsed = moment.utc(value, CALENDAR_DATE_PARSE_FORMATS, true);
  return parsed.isValid() ? parsed : null;
};

/**
 * Format a calendar-day value for display without timezone day shifts.
 * Returns null for empty/invalid values.
 */
export const formatCalendarDateUtc = (
  value: ?(string | Date),
  format: string = 'MM/DD/YYYY',
): ?string => {
  const parsed = parseCalendarDateUtc(value);
  return parsed ? parsed.format(format) : null;
};

/**
 * Parses time values that might be Moment objects or time strings.
 * Time strings like "03:30 PM" are converted to full datetime Moment objects
 * by adding today's date, since redux-form and other parts of the app
 * serialize Moment objects as time-only strings but validation and calculations
 * require full datetime objects.
 *
 * @param {any} time - A Moment object, time string (e.g., "03:30 PM"), or null/undefined
 * @returns {Moment|null} - A Moment datetime object or null
 */
export const parseTimeValue = (time) => {
  if (!time) return null;

  if (moment.isMoment(time)) {
    return time.isValid() ? time : null;
  }

  if (typeof time === 'string') {
    const today = moment().format('YYYY-MM-DD');
    const dateTimeString = `${today} ${time}`;

    // Try strict parsing with common formats first
    const formats = [
      'YYYY-MM-DD hh:mm A',
      'YYYY-MM-DD h:mm A',
      'YYYY-MM-DD HH:mm',
      'YYYY-MM-DD H:mm',
    ];

    for (let i = 0; i < formats.length; i += 1) {
      const parsed = moment(dateTimeString, formats[i], true);
      if (parsed.isValid()) {
        return parsed;
      }
    }

    // Fall back to lenient parsing for flexibility
    const parsed = moment(dateTimeString);
    return parsed.isValid() ? parsed : null;
  }

  const parsed = moment(time);
  return parsed.isValid() ? parsed : null;
};

export function isBetweenDates(
  testedDate: Date,
  startDate: Date,
  endDate: Date,
  precision?: string,
): boolean {
  return (
    startDate.isSameOrBefore(testedDate, precision) &&
    testedDate.isSameOrBefore(endDate, precision)
  );
}

export const isValidDate = (dateStr: string) =>
  typeof dateStr === 'string' && moment(dateStr).isValid();

export const validateDateRange = ({
  field,
  date,
  dateFrom,
  dateTo,
}: {
  field: string,
  date: Object,
  dateFrom: string,
  dateTo: string,
}) => {
  let momentFrom, momentTo, validDateFrom, validDateTo;
  if (field === 'dateFrom' && dateTo) {
    momentFrom = moment(date);
    momentTo = moment(dateTo);
    validDateFrom = date && momentFrom.isValid();
    validDateTo = dateTo && momentTo.isValid();
  } else if (field === 'dateTo' && dateFrom) {
    momentFrom = moment(dateFrom);
    momentTo = moment(date);
    validDateFrom = dateFrom && momentFrom.isValid();
    validDateTo = date && momentTo.isValid();
  }
  const datesFormatted = validDateFrom && validDateTo;
  const fromAfterTo = momentFrom && momentFrom.isAfter(momentTo);

  return { datesFormatted, fromAfterTo };
};

export const validateDateFormat = ({
  field,
  date,
}: {
  field: string,
  date: Object,
}) => {
  let dateErrors = {
    [`${field}Error`]: '',
  };

  if (!(date && moment(date).isValid())) {
    dateErrors = {
      [`${field}Error`]: 'Must be a valid date',
    };
  } else if (date && moment(date).isValid()) {
    dateErrors = {
      [`${field}Error`]: '',
    };
  }

  return dateErrors;
};

export const isToDateNotSameOrAfterFromDate = (
  fromDate?: string,
  toDate?: string,
) => {
  if (!fromDate || !toDate) return false;

  const momentFrom = moment(fromDate);
  const momentTo = moment(toDate);

  if (!momentFrom.isValid() || !momentTo.isValid()) return false;

  return !momentTo.isSameOrAfter(momentFrom);
};

export const sortByDateField = (
  data: Array<any>,
  dateFieldName: string,
  desc?: boolean = false,
) => {
  return data.sort((a, b) => {
    if (desc) {
      return moment.utc(b[dateFieldName]).diff(moment.utc(a[dateFieldName]));
    }
    return moment.utc(a[dateFieldName]).diff(moment.utc(b[dateFieldName]));
  });
};

export const disableAllDaysOfTheMonthExceptFirst = (date: Date) => {
  if (date) {
    const dayOfMonth = new Date(date).getDate();
    return dayOfMonth !== 1;
  }
};

export const isNextActivityLate = (activity) => {
  if (!activity?.startTime) return false;
  return moment()
    .startOf('day')
    .isAfter(moment(activity.startTime).startOf('day'));
};

export const stringDateToFormattedDate = (
  value: string,
  format: string,
): string => {
  return moment(value).format(format);
};
